import { useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { cerrarSesion } from '../../lib/cerrarSesion';
import { SETUP_TOTAL_STEPS } from './accountSetup';
import { ONBOARDING_V2 } from './onboardingV2Routes';
import { useUserGroup } from '../../hooks/useUserGroup';
import { MarcoDelOnboarding } from './marca/MarcoDelOnboarding';

/**
 * Layout de los 3 pasos del cuestionario corto: nombre, rubro y producto
 * (ADR-2026-10-31-onboarding-corto-para-la-prueba). Desde
 * ADR-2026-10-31-el-cuestionario-corto-con-el-diseno-del-de-marca se ve como el onboarding
 * de marca: el mismo marco (`MarcoDelOnboarding`) con "Paso N de 3", y cada paso con Cumbre.
 *
 * `generando` y `resultados` quedan **fuera** de este layout: no tienen pasos, y montan el
 * marco por su cuenta.
 */

// Nombre → rubro → producto (ADR-2026-10-31-onboarding-corto-para-la-prueba).
const PASOS: string[] = [ONBOARDING_V2.setup, ONBOARDING_V2.setupCategoria, ONBOARDING_V2.setupProducto];
const NOMBRE_DEL_PASO = ['Tu marca', 'Tu rubro', 'Tu producto'];

const pasoDe = (pathname: string): number => Math.max(0, PASOS.indexOf(pathname));

export function AccountSetupLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const paso = pasoDe(location.pathname);

  // Se monta por su EFECTO, no por lo que devuelve: arranca —una sola vez, al entrar al
  // cuestionario— la lectura de la empresa que deja el PLAN cacheado en localStorage. El
  // paso del producto y la pantalla de resultados lo necesitan para no hablarle de la prueba
  // gratis a quien acaba de pagar; si lo pidieran recién al llegar, Cumbre empezaría a decir
  // el copy equivocado. Ver ADR-2026-09-22-el-cuestionario-no-le-vende-a-quien-ya-pago.
  useUserGroup();

  // Cuántas entradas de historial apiló EL PROPIO cuestionario por encima de aquella
  // con la que se entró. Es lo único que habilita un `navigate(-1)` seguro: garantiza
  // que la entrada de abajo es el paso anterior y no una pantalla ajena.
  const pasosApilados = useRef(0);
  const rutaAnterior = useRef(location.pathname);
  useEffect(() => {
    if (rutaAnterior.current === location.pathname) return;
    pasosApilados.current =
      pasoDe(location.pathname) > pasoDe(rutaAnterior.current)
        ? pasosApilados.current + 1
        : Math.max(0, pasosApilados.current - 1);
    rutaAnterior.current = location.pathname;
  }, [location.pathname]);

  const saliendo = useRef(false);
  const volver = () => {
    // Volver desde el paso 1 = salir del onboarding, y salir es DESLOGUEARSE: detrás sólo
    // está el registro, y con la sesión viva la guardia de `/auth` rebotaba al home
    // (ADR-2026-08-03-volver-en-el-paso-1-desloguea). `cerrarSesion` además borra las
    // respuestas del cuestionario, así la próxima cuenta del dispositivo no las hereda.
    if (paso === 0) {
      if (saliendo.current) return; // el signOut tarda: un segundo toque no debe reentrar
      saliendo.current = true;
      void cerrarSesion(ONBOARDING_V2.bienvenida);
      return;
    }
    // Sólo POP si la entrada de abajo la apilamos nosotros; si no (deep-link o pestaña
    // restaurada en un paso >1), por ruta. Ver ADR-793.
    if (pasosApilados.current > 0) navigate(-1);
    else navigate(PASOS[paso - 1]);
  };

  return (
    <MarcoDelOnboarding
      pasos={{
        actual: paso,
        total: SETUP_TOTAL_STEPS,
        nombre: NOMBRE_DEL_PASO[paso],
        accion: (
          <button type="button" onClick={volver} className="om-salir">
            {paso === 0 ? 'Salir' : 'Volver'}
          </button>
        ),
      }}
    >
      <main key={location.pathname} className="om-pantalla">
        <Outlet />
      </main>
    </MarcoDelOnboarding>
  );
}

export default AccountSetupLayout;
